import { useState, type FormEvent } from 'react';
import { UserPlus, Clock, X } from 'lucide-react';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { useAuth } from '../contextos/AuthContext';
import {
  alterarPapel,
  cancelarConvite,
  convidar,
  definirAtivo,
  DESCRICAO_PAPEL,
  ROTULO_PAPEL,
  useConvites,
  useUsuarios,
} from '../servicos/usuarios';
import type { Papel } from '../tipos';

const PAPEIS_CONVIDAVEIS: Exclude<Papel, 'AGENTE'>[] = ['OPERADOR', 'CONSULTA', 'ADMINISTRADOR'];

export function Usuarios() {
  const { usuarios, carregando, erro } = useUsuarios();
  const { convites } = useConvites();
  const { perfil } = useAuth();

  const pendentes = convites.filter((c) => !c.aceitoEm);
  const agora = new Date();
  const administradoresAtivos = usuarios.filter((u) => u.papel === 'ADMINISTRADOR' && u.ativo).length;

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar os usuários" detalhe={erro} />;

  return (
    <div className="flex flex-col gap-6">
      <Cabecalho
        titulo="Usuários autorizados"
        descricao="Quem tem acesso e com que perfil. Não existe cadastro aberto: só entra quem for convidado."
      />

      <FormularioConvite />

      <section className="flex flex-col gap-3">
        <h2 className="font-mono text-[0.65rem] tracking-[0.1em] text-marca uppercase">
          Com acesso ({usuarios.length})
        </h2>
        <div className="overflow-x-auto rounded-md border border-regua-2">
          <table className="w-full min-w-[38rem] border-collapse bg-superficie text-sm">
            <thead>
              <tr>
                {['Nome', 'E-mail', 'Perfil', 'Último acesso', 'Situação'].map((h) => (
                  <th
                    key={h}
                    className="border-b border-regua-2 px-3 py-2 text-left font-mono text-[0.62rem] font-semibold tracking-wider text-marca uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => {
                const ehEu = u.id === perfil?.id;
                // Rebaixar ou desativar o último administrador ativo deixaria o
                // sistema sem quem administra — e sem caminho de volta.
                const ultimoAdmin = u.papel === 'ADMINISTRADOR' && u.ativo && administradoresAtivos === 1;
                return (
                  <tr key={u.id} className="border-b border-regua last:border-0 even:bg-marca/4">
                    <td className="px-3 py-2">
                      {u.nome}
                      {ehEu && <span className="ml-1.5 text-[0.7rem] text-tinta-3">(você)</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-tinta-2">{u.email}</td>
                    <td className="px-3 py-2">
                      <select
                        value={u.papel}
                        disabled={ehEu || ultimoAdmin}
                        onChange={(e) => void alterarPapel(u.id, e.target.value as Papel)}
                        className="rounded border border-regua-2 bg-papel px-2 py-1 text-xs disabled:opacity-60"
                        title={
                          ehEu
                            ? 'Você não pode alterar o próprio perfil'
                            : ultimoAdmin
                              ? 'É o único administrador ativo'
                              : undefined
                        }
                      >
                        {PAPEIS_CONVIDAVEIS.map((p) => (
                          <option key={p} value={p}>
                            {ROTULO_PAPEL[p]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-tinta-2">
                      {u.ultimoAcesso ? u.ultimoAcesso.toDate().toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        disabled={ehEu || ultimoAdmin}
                        onClick={() => void definirAtivo(u.id, !u.ativo)}
                        className={`rounded-full border px-2 py-0.5 font-mono text-[0.62rem] font-semibold tracking-wider uppercase disabled:opacity-60 ${
                          u.ativo ? 'border-c-via text-c-via' : 'border-tinta-3 text-tinta-3'
                        }`}
                      >
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="font-mono text-[0.65rem] tracking-[0.1em] text-marca uppercase">
          Convites pendentes ({pendentes.length})
        </h2>
        {pendentes.length === 0 ? (
          <Vazio
            titulo="Nenhum convite aguardando"
            descricao="Convide alguém pelo formulário acima. O e-mail sai na hora, com um link que vale 7 dias — é o link que libera o acesso, e ele serve uma vez só."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {pendentes.map((c) => {
              const vence = c.expiraEm?.toDate?.() ?? null;
              const expirado = vence !== null && vence < agora;
              return (
                <li
                  key={c.id}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-regua-2 bg-superficie px-3 py-2 text-sm"
                >
                  <Clock className={`size-4 ${expirado ? 'text-pend' : 'text-tinta-3'}`} aria-hidden />
                  <span className="font-mono text-xs">{c.email}</span>
                  <span className="rounded bg-marca/10 px-1.5 py-0.5 font-mono text-[0.6rem] tracking-wider text-marca uppercase">
                    {ROTULO_PAPEL[c.papel]}
                  </span>
                  <span className={`text-xs ${expirado ? 'font-medium text-pend' : 'text-tinta-3'}`}>
                    {expirado
                      ? 'expirado'
                      : vence
                        ? `expira em ${vence.toLocaleDateString('pt-BR')}`
                        : 'sem validade'}
                  </span>
                  <button
                    type="button"
                    onClick={() => void cancelarConvite(c.email)}
                    className="ml-auto inline-flex items-center gap-1 rounded border border-regua-2 px-2 py-1 text-xs text-tinta-2 hover:bg-superficie-2"
                  >
                    <X className="size-3" aria-hidden />
                    Cancelar
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function FormularioConvite() {
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState<Exclude<Papel, 'AGENTE'>>('OPERADOR');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setOk(null);
    setEnviando(true);
    try {
      await convidar(email, papel);
      // O convite agora É o e-mail: se a mensagem não sai, a função nem grava o
      // convite. Então dá para afirmar que foi enviado, em vez de pedir que o
      // administrador avise a pessoa por fora.
      setOk(`Convite enviado para ${email.trim().toLowerCase()}. O link vale 7 dias.`);
      setEmail('');
    } catch (ex) {
      setErro((ex as Error).message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form
      onSubmit={enviar}
      className="flex flex-col gap-3 rounded-md border border-regua-2 bg-superficie p-4"
    >
      <h2 className="flex items-center gap-2 font-mono text-[0.65rem] tracking-[0.1em] text-marca uppercase">
        <UserPlus className="size-3.5" aria-hidden />
        Convidar
      </h2>

      <div className="flex flex-wrap gap-3">
        <label className="flex min-w-56 flex-1 flex-col gap-1">
          <span className="text-xs font-medium">E-mail</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
          />
        </label>
        <label className="flex min-w-44 flex-col gap-1">
          <span className="text-xs font-medium">Perfil</span>
          <select
            value={papel}
            onChange={(e) => setPapel(e.target.value as Exclude<Papel, 'AGENTE'>)}
            className="rounded border border-regua-2 bg-papel px-3 py-1.5 text-sm outline-none focus:border-marca"
          >
            {PAPEIS_CONVIDAVEIS.map((p) => (
              <option key={p} value={p}>
                {ROTULO_PAPEL[p]}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={enviando}
            className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
          >
            {enviando ? 'Enviando…' : 'Enviar convite'}
          </button>
        </div>
      </div>

      <p className="text-xs text-tinta-3">{DESCRICAO_PAPEL[papel]}</p>
      {erro && <Erro titulo={erro} />}
      {ok && (
        <p className="rounded border-l-3 border-marca bg-marca/10 px-3 py-2 text-sm text-tinta-2">{ok}</p>
      )}
    </form>
  );
}
